/**
 * Auth configuration.
 *
 * Supabase is configured through Vite environment variables. When they are
 * absent — a fresh clone, or a preview build without secrets — the app must
 * still run: the curriculum, the Math Map, lessons and practice are all usable
 * without an account, and only the account routes degrade.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import {
  classifyKey,
  createSupabaseAuthProvider,
  PrivilegedKeyError,
  SUPABASE_CLIENT_OPTIONS,
  type AuthProvider,
} from '@yuan-math/auth';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * Whether a privileged key was supplied where a publishable one belongs.
 *
 * Reported as data rather than thrown. A module-scope `throw` propagates during
 * import evaluation, which happens *before* any caller's try/catch body runs —
 * so the app would die with a blank page and a console message nobody reads.
 * Returning the fact lets `main.tsx` render an explanation instead.
 */
export const privilegedKeyDetected = anonKey !== undefined && classifyKey(anonKey) === 'secret';

export const privilegedKeyMessage = privilegedKeyDetected ? new PrivilegedKeyError().message : '';

/**
 * The publishable key is meant to be public; the secret key bypasses row-level
 * security, and a build carrying one would expose every account. The two sit
 * next to each other in the Supabase dashboard, so this is an easy mistake to
 * make exactly once — and it must not start the app when it happens.
 */
export const authConfigured = Boolean(url && anonKey) && !privilegedKeyDetected;

/**
 * The one Supabase client in the browser.
 *
 * Emphatically one. Two clients over the same project share a storage key and
 * each runs `detectSessionInUrl` when it is constructed, so both would race to
 * spend the single-use code in an activation link — and the loser of that race
 * is what reported a good link as "that link did not work". supabase-js warns
 * about the same thing from the other side ("Multiple GoTrueClient instances
 * detected in the same browser context"). Built eagerly, because the auth
 * provider below is eager, and a lazily-built second one was the bug.
 */
const client: SupabaseClient | null = authConfigured
  ? createClient(url as string, anonKey as string, SUPABASE_CLIENT_OPTIONS)
  : null;

/**
 * The raw client, for data access outside authentication.
 *
 * `storage.ts` uses it to sync learner progress; nothing else in the
 * application should reach for it, so that persistence stays in one file.
 */
export function supabaseClient(): SupabaseClient | null {
  return client;
}

/**
 * The provider, or null when unconfigured.
 *
 * Null is a first-class state rather than a thrown error: the UI checks it and
 * explains that accounts are not available on this deployment, which is far
 * better than a blank page.
 */
export const authProvider: AuthProvider | null =
  authConfigured && client
    ? createSupabaseAuthProvider(
        {
          url: url as string,
          anonKey: anonKey as string,
          // Supabase requires this to be an allowed redirect URL in the dashboard.
          redirectTo: `${window.location.origin}/activate`,
          resetRedirectTo: `${window.location.origin}/reset`,
        },
        client,
      )
    : null;
